import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { canOpenRoute } from "../lib/roles";
import { Package, Warehouse, TrendingUp, AlertTriangle, XCircle, ArrowDownToLine, ArrowUpFromLine, Shuffle } from "lucide-react";

function Kpi({ label, value, icon: Icon, accent = "cyan", testId, onClick }) {
  // Final Visual Polish 2026-08-31: alle KPI-Kacheln teilen dieselbe
  // Surface-Basis (`bg-card`) — shadcn-Token → #ffffff im Light-Theme,
  // dark card im Dark-Theme. Vorher `bg-zinc-900 hairline`, was zu
  // uneinheitlich hellen Kacheln im White-Theme führte. Akzent bleibt
  // über Border/Icon/Text erhalten.
  const colors = {
    cyan: "text-cyan-400 border-cyan-400/30",
    green: "text-emerald-400 border-emerald-400/30",
    red: "text-rose-400 border-rose-400/30",
    yellow: "text-yellow-400 border-yellow-400/30",
    zinc: "text-zinc-100 border-zinc-800",
  }[accent];
  const isClickable = typeof onClick === "function";
  const Tag = isClickable ? "button" : "div";
  return (
    <Tag
      data-testid={testId}
      onClick={onClick}
      type={isClickable ? "button" : undefined}
      className={`text-left border ${colors} bg-card p-4 sm:p-5 rounded-sm min-w-0 overflow-hidden w-full ${isClickable ? "cursor-pointer hover:bg-zinc-800 transition-colors" : ""}`}
    >
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="text-xs uppercase tracking-widest text-zinc-500 truncate">{label}</div>
        <Icon size={16} className={`${colors.split(" ")[0]} shrink-0`} />
      </div>
      <div className="font-display text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight tabular-nums truncate">
        {value}
      </div>
    </Tag>
  );
}

const TX_LABEL = {
  GOODS_RECEIPT: "Wareneingang",
  GOODS_ISSUE: "Warenausgang",
  TRANSFER: "Umlagerung",
  INVENTORY_CORRECTION: "Korrektur",
  VENDING_MACHINE_SALE: "Automaten-Verkauf",
  POS_SALE: "Kassenverkauf",
  RETURN: "Retoure",
};
const TX_COLOR = {
  GOODS_RECEIPT: "text-emerald-400",
  GOODS_ISSUE: "text-rose-400",
  TRANSFER: "text-violet-400",
  INVENTORY_CORRECTION: "text-yellow-400",
};
// Operations Polish 2026-08-30: dezente Zeilen-/Badge-Farbcodes für die
// „Letzte Buchungen"-Liste. Alpha-basiert → theme-agnostisch. Legende
// darunter erklärt die Farbzuordnung. Keine Vollflächen-Färbung.
const TX_ROW_BG = {
  GOODS_RECEIPT: "border-l-2 border-emerald-500/50 bg-emerald-500/[0.04]",
  GOODS_ISSUE: "border-l-2 border-rose-500/50 bg-rose-500/[0.04]",
  TRANSFER: "border-l-2 border-violet-500/50 bg-violet-500/[0.04]",
};

export default function Dashboard() {
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const kpi = useQuery({
    queryKey: ["kpis"],
    queryFn: async () => (await api.get("/dashboard/kpis")).data,
  });
  const recent = useQuery({
    queryKey: ["recent"],
    queryFn: async () => (await api.get("/dashboard/recent-activity")).data,
  });
  const k = kpi.data || {};

  const goToStockFilter = (mode) => nav(`/lagerbestand?stock=${mode}`);
  const goToHistory = (type) => {
    const today = new Date().toISOString().slice(0, 10);
    nav(`/buchungshistorie?date_from=${today}&date_to=${today}&type=${type}`);
  };

  return (
    <div data-testid="dashboard-page">
      <div className="mb-6 sm:mb-8">
        <div className="text-xs uppercase tracking-widest text-cyan-400">Übersicht</div>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight uppercase">
          Dashboard
        </h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
        <Kpi testId="kpi-total-products" label="Produkte" value={k.total_products ?? "–"} icon={Package}
             onClick={() => nav("/produkte")} />
        {isAdmin && canOpenRoute(user, "/lagerorte") && (
          <Kpi testId="kpi-locations" label="Lagerorte" value={k.total_locations ?? "–"} icon={Warehouse} accent="zinc"
               onClick={() => nav("/lagerorte")} />
        )}
        <Kpi
          testId="kpi-inventory-value"
          label="Bestandswert"
          value={
            k.inventory_value != null
              ? `€ ${Number(k.inventory_value).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
              : "–"
          }
          icon={TrendingUp}
        />
        <Kpi testId="kpi-low-stock" label="Niedriger Bestand" value={k.low_stock_count ?? "–"} icon={AlertTriangle} accent="yellow"
             onClick={() => goToStockFilter("low")} />
        <Kpi testId="kpi-out-of-stock" label="Kein Bestand" value={k.out_of_stock_count ?? "–"} icon={XCircle} accent="red"
             onClick={() => goToStockFilter("out")} />
        <Kpi testId="kpi-receipts-today" label="WE heute" value={k.receipts_today ?? "–"} icon={ArrowDownToLine} accent="green"
             onClick={() => goToHistory("GOODS_RECEIPT")} />
        <Kpi testId="kpi-issues-today" label="WA heute" value={k.issues_today ?? "–"} icon={ArrowUpFromLine} accent="red"
             onClick={() => goToHistory("GOODS_ISSUE")} />
      </div>

      <div className="border border-zinc-800 bg-zinc-900 rounded-sm">
        <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between flex-wrap gap-3">
          <div className="font-display text-xl uppercase tracking-wide font-bold">Letzte Buchungen</div>
          <div
            data-testid="dashboard-tx-legend"
            className="flex flex-wrap items-center gap-2 sm:gap-3 text-[10px] uppercase tracking-widest text-zinc-400"
          >
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 rounded-full bg-emerald-500/80" /> Wareneingang
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 rounded-full bg-rose-500/80" /> Warenausgang
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 rounded-full bg-violet-500/80" /> Umlagerung
            </span>
          </div>
        </div>
        <div className="divide-y divide-zinc-800" data-testid="recent-activity-list">
          {(recent.data || []).length === 0 && (
            <div className="p-6 text-sm text-zinc-500">Noch keine Buchungen.</div>
          )}
          {(recent.data || []).map((t) => (
            <div
              key={t.id}
              data-testid={`recent-row-${t.id}`}
              data-tx-type={t.type}
              className={`px-4 sm:px-5 py-3 flex items-center justify-between gap-3 sm:gap-4 ${TX_ROW_BG[t.type] || ""}`}
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm truncate">{t.product_name || "—"}</div>
                <div className="text-xs text-zinc-500 font-mono truncate">
                  {new Date(t.created_at).toLocaleString("de-DE")} · {t.user_email || "system"}
                </div>
              </div>
              <div className={`text-[10px] sm:text-xs uppercase tracking-wider shrink-0 hidden sm:block ${TX_COLOR[t.type] || "text-zinc-300"}`}>
                {TX_LABEL[t.type] || t.type}
              </div>
              <div className="font-mono tabular-nums text-base sm:text-lg w-14 sm:w-16 text-right shrink-0">
                {t.type === "GOODS_ISSUE" ? "-" : t.type === "GOODS_RECEIPT" ? "+" : ""}
                {t.quantity}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
